import {
  archiveEmail,
  batchArchiveEmails,
  bookingEmailLabel,
  getMonitoring,
  getPaginated,
} from "@/services/ai";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import moment from "moment";
import { Archive, Loader2 } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationPrevious,
  PaginationLink,
  PaginationNext,
} from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import { useAuthContext } from "@/context/AuthContext";
import { DatePicker } from "@/components/form_fields/DatePicker";
import { Skeleton } from "@/components/ui/skeleton";

const PAGE_SIZE = 10;

const createInitialStartDate = () =>
  moment().subtract(2, "days").startOf("day").toDate();

const createInitialEndDate = () => moment().endOf("day").toDate();

const getDateParams = (startDate?: Date, endDate?: Date) => {
  if (!startDate) {
    return {};
  }

  return {
    startDate: moment(startDate).startOf("day").toDate(),
    endDate: moment(endDate ?? startDate).endOf("day").toDate(),
  };
};

const getPaginationItems = (currentPage: number, pageCount: number) => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  const pages = new Set([1, pageCount, currentPage]);

  if (currentPage > 1) {
    pages.add(currentPage - 1);
  }

  if (currentPage < pageCount) {
    pages.add(currentPage + 1);
  }

  if (currentPage <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }

  if (currentPage >= pageCount - 2) {
    pages.add(pageCount - 3);
    pages.add(pageCount - 2);
    pages.add(pageCount - 1);
  }

  return Array.from(pages)
    .filter((page) => page >= 1 && page <= pageCount)
    .sort((a, b) => a - b)
    .reduce<(number | "ellipsis")[]>((items, page) => {
      const previousPage = items[items.length - 1];

      if (typeof previousPage === "number" && page - previousPage > 1) {
        items.push("ellipsis");
      }

      items.push(page);
      return items;
    }, []);
};

const MonitoringSkeleton = () => (
  <div className="flex flex-col gap-4">
    <div className="grid min-w-0 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className="rounded-lg border p-4 shadow-sm">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="mt-3 h-8 w-14" />
        </div>
      ))}
    </div>
    <div className="flex min-w-0 flex-wrap gap-2">
      {Array.from({ length: 5 }, (_, index) => (
        <Skeleton key={index} className="h-8 w-28 rounded-full" />
      ))}
    </div>
  </div>
);

const EmailListSkeleton = () => (
  <div className="flex min-w-0 flex-col gap-4">
    <div className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-9 w-44" />
    </div>
    {Array.from({ length: 6 }, (_, index) => (
      <div
        key={index}
        className="flex min-w-0 flex-col gap-3 rounded-lg border p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Skeleton className="h-4 w-4 shrink-0" />
          <Skeleton className="h-6 w-24 shrink-0 rounded-full" />
          <Skeleton className="h-4 min-w-0 flex-1" />
        </div>
        <div className="flex shrink-0 items-center gap-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-8 w-8" />
        </div>
      </div>
    ))}
  </div>
);

type bookingErrorType =
  | "missingAlias"
  | "creationDate"
  | "cancellation"
  | "checkInOut"
  | "refMissing"
  | "priceMismatch"
  | "tooLong"
  | "late"
  | "unknown";

type bookingEmailStatus = "pending" | "success" | "error";

export const parsedContentSchema = z.object({
  _createdAt: z.string(),
  collectionId: z.string(),
  lang: z.string(),
  owner: z.string(),
  originalPrice: z.number(),
  lockDate: z.string(),
  guests: z
    .object({
      firstName: z.string(),
      secondName: z.string(),
      email: z.string(),
    })
    .array(),
  payload: z.object({
    reference: z.string(),
    checkin: z.string(),
    checkout: z.string(),
    board: z.string().optional(),
    rooms: z
      .object({
        name: z.string(),
        guestsAdults: z.number(),
        guestsKids: z.number(),
        amenities: z.string(),
      })
      .array(),

    extra: z.string().optional(),
  }),
  bookedWithLoyaltyCode: z.string().optional(),
  reservationType: z.string(),
});

export type bookingEmailType = {
  _id: string;
  _createdAt: Date;
  emailId: string;
  encryptedContent: { headers: { subject: string }; text: string };
  hashedContent: string;
  step: number;
  status: bookingEmailStatus;
  errorType?: bookingErrorType;
  hotelAlias: string;
  labels: bookingEmailLabel[];
  encryptedParsedContent:
    | z.infer<typeof parsedContentSchema>
    | "No data"
    | "Ofuscated data";
  expiresAt: Date;
};

const Ai = () => {
  const [monitoring, setMonitoring] = useState<{
    total: number;
    success: number;
    pending: number;
    error: number;
    errorTypes: Record<bookingErrorType, number>;
  }>();
  const [monitoringLoading, setMonitoringLoading] = useState(false);
  const [monitoringError, setMonitoringError] = useState(false);

  const [emails, setEmails] = useState<bookingEmailType[]>();
  const [emailsLoading, setEmailsLoading] = useState(true);
  const [emailsPage, setEmailsPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalEmails, setTotalEmails] = useState(0);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);

  const [startDate, setStartDate] = useState<Date>(() =>
    createInitialStartDate()
  );
  const [endDate, setEndDate] = useState<Date>(() => createInitialEndDate());

  const [errorTypes, setErrorTypes] = useState<bookingErrorType[]>([]);

  const { userId } = useAuthContext();

  const [shiftHeld, setShiftHeld] = useState(false);

  const visibleEmailIds = useMemo(
    () => emails?.map((email) => email._id) ?? [],
    [emails]
  );

  const allVisibleSelected =
    visibleEmailIds.length > 0 &&
    visibleEmailIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleEmailIds.some((id) =>
    selectedIds.has(id)
  );

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate =
      someVisibleSelected && !allVisibleSelected;
  }, [allVisibleSelected, someVisibleSelected]);

  function downHandler(e: KeyboardEvent) {
    if (e.key === "Shift") {
      setShiftHeld(true);
    }
  }

  function upHandler(e: KeyboardEvent) {
    if (e.key === "Shift") {
      setShiftHeld(false);
    }
  }

  function blurHandler() {
    setShiftHeld(false);
  }

  useEffect(() => {
    window.addEventListener("keydown", downHandler);
    window.addEventListener("keyup", upHandler);
    window.addEventListener("blur", blurHandler);
    return () => {
      window.removeEventListener("keydown", downHandler);
      window.removeEventListener("keyup", upHandler);
      window.removeEventListener("blur", blurHandler);
    };
  }, []);

  const fetchMonitoring = async () => {
    setMonitoringLoading(true);
    try {
      const res = await getMonitoring({
        ...getDateParams(startDate, endDate),
      });
      setMonitoring(res);
      setMonitoringError(false);
    } catch {
      setMonitoring(undefined);
      setMonitoringError(true);
    }
    setMonitoringLoading(false);
  };

  const fetchEmails = async (page: number, eTypes: string[]) => {
    setEmailsLoading(true);
    try {
      const res = await getPaginated({
        page: page,
        size: PAGE_SIZE,
        ...getDateParams(startDate, endDate),
        errorTypes: eTypes,
        labels: ["inbox"],
      });
      setEmails(res.data);
      setEmailsPage(Math.max(1, Math.ceil(res.page || page)));
      setTotalEmails(res.total ?? 0);
      setTotalPages(Math.ceil((res.total ?? 0) / (res.size || PAGE_SIZE)));
    } catch {
      setEmails(undefined);
      setTotalEmails(0);
      setTotalPages(0);
    } finally {
      setSelectedIds(new Set());
      setLastSelectedId(null);
      setEmailsLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchMonitoring();
    }
  }, [userId]);

  useEffect(() => {
    fetchEmails(emailsPage, errorTypes);
  }, [emailsPage, errorTypes]);

  const refreshData = () => {
    fetchMonitoring();

    if (emailsPage === 1) {
      fetchEmails(1, errorTypes);
      return;
    }

    setEmailsPage(1);
  };

  const toggleErrorType = (errorType: bookingErrorType) => {
    setErrorTypes((previousErrorTypes) => {
      if (previousErrorTypes.includes(errorType)) {
        return previousErrorTypes.filter((type) => type !== errorType);
      }

      return [...previousErrorTypes, errorType];
    });
    setEmailsPage(1);
  };

  const toggleEmailSelection = (
    emailId: string,
    checked: boolean,
    index: number
  ) => {
    setSelectedIds((previousSelectedIds) => {
      const nextSelectedIds = new Set(previousSelectedIds);
      const anchorIndex = lastSelectedId
        ? visibleEmailIds.indexOf(lastSelectedId)
        : -1;
      const rangeIds =
        shiftHeld && anchorIndex !== -1
          ? visibleEmailIds.slice(
              Math.min(anchorIndex, index),
              Math.max(anchorIndex, index) + 1
            )
          : [emailId];

      rangeIds.forEach((id) => {
        if (checked) {
          nextSelectedIds.add(id);
          return;
        }

        nextSelectedIds.delete(id);
      });

      return nextSelectedIds;
    });
    setLastSelectedId(emailId);
  };

  const toggleVisibleSelection = (checked: boolean) => {
    setSelectedIds((previousSelectedIds) => {
      const nextSelectedIds = new Set(previousSelectedIds);

      visibleEmailIds.forEach((id) => {
        if (checked) {
          nextSelectedIds.add(id);
          return;
        }

        nextSelectedIds.delete(id);
      });

      return nextSelectedIds;
    });
    setLastSelectedId(null);
  };

  const selectedCount = selectedIds.size;
  const completedTotal = (monitoring?.total ?? 0) - (monitoring?.pending ?? 0);
  const failureRate =
    monitoring && completedTotal > 0
      ? (monitoring.error / completedTotal) * 100
      : 0;
  const successRate =
    monitoring && completedTotal > 0
      ? (monitoring.success / completedTotal) * 100
      : 0;
  const paginationItems = getPaginationItems(emailsPage, totalPages);
  const isInitialEmailsLoading = emailsLoading && !emails;
  const isRefreshingEmails = emailsLoading && Boolean(emails);
  const isInitialMonitoringLoading = monitoringLoading && !monitoring;
  const isRefreshingMonitoring = monitoringLoading && Boolean(monitoring);
  const isRefreshing = emailsLoading || monitoringLoading;

  return (
    <div className="flex min-w-0 max-w-full flex-col gap-12 overflow-x-hidden">
      <div className="flex min-w-0 flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">
              Data inizio
            </span>
            <DatePicker
              value={startDate}
              onChange={(date) => {
                setStartDate(date);
                if (moment(date).isAfter(endDate, "day")) {
                  setEndDate(moment(date).endOf("day").toDate());
                }
              }}
              placeholder="Seleziona inizio"
              className="w-full sm:w-fit"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Data fine</span>
            <DatePicker
              value={endDate}
              onChange={(date) => {
                setEndDate(date);
                if (moment(date).isBefore(startDate, "day")) {
                  setStartDate(moment(date).startOf("day").toDate());
                }
              }}
              placeholder="Seleziona fine"
              className="w-full sm:w-fit"
            />
          </div>
          <Button
            variant="takyon"
            onClick={refreshData}
            disabled={!startDate || !endDate || isRefreshing}
            className="sm:mt-6"
          >
            {isRefreshing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Aggiorno
              </>
            ) : (
              "Aggiorna"
            )}
          </Button>
        </div>
        <div>
          <Link to="/admin/archived_ai">Vedi archivio</Link>
        </div>
      </div>
      {isInitialMonitoringLoading ? (
        <MonitoringSkeleton />
      ) : monitoring ? (
        <div
          className={`flex flex-col gap-4 transition-opacity ${
            isRefreshingMonitoring ? "opacity-60" : ""
          }`}
        >
          <div className="grid min-w-0 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {(Object.keys(monitoring) as (keyof typeof monitoring)[]).map(
              (key, i) => {
                if (key === "errorTypes") {
                  return null;
                }
                return (
                  <div key={i} className="rounded-lg border p-4 shadow-sm">
                    <p className="text-sm text-muted-foreground">{key}</p>
                    <p className="text-2xl font-semibold">
                      {monitoring[key]}
                    </p>
                  </div>
                );
              }
            )}

            <div
              className="rounded-lg border p-4 shadow-sm"
              style={{
                backgroundColor: failureRate <= 2 ? "#9AFEE3" : "#ff746c",
              }}
            >
              <p className="text-sm text-muted-foreground">failure rate</p>
              <p className="text-2xl font-semibold">
                {failureRate.toFixed(2)}%
              </p>
            </div>
            <div
              className="rounded-lg border p-4 shadow-sm"
              style={{
                backgroundColor: successRate >= 98 ? "#9AFEE3" : "#ff746c",
              }}
            >
              <p className="text-sm text-muted-foreground">success rate</p>
              <p className="text-2xl font-semibold">
                {successRate.toFixed(2)}%
              </p>
            </div>
          </div>
          <div className="flex min-w-0 flex-wrap gap-2">
            {(
              Object.keys(
                monitoring.errorTypes
              ) as (keyof typeof monitoring.errorTypes)[]
            ).map((key, i) => (
              <button
                className="rounded-full border px-3 py-1 text-sm shadow-sm"
                style={{
                  backgroundColor: errorTypes.includes(key) ? "#444" : "",
                  color: errorTypes.includes(key) ? "white" : "",
                }}
                key={i}
                disabled={monitoringLoading}
                onClick={() => toggleErrorType(key)}
              >
                {key}: {monitoring.errorTypes[key]}
              </button>
            ))}
          </div>
        </div>
      ) : monitoringError ? (
        <div className="rounded-lg border p-4 text-sm text-muted-foreground">
          Non riesco a caricare il riepilogo in questo momento.
        </div>
      ) : (
        null
      )}

      {isInitialEmailsLoading ? (
        <EmailListSkeleton />
      ) : emails && emails.length > 0 ? (
        <>
          <div
            className={`flex min-w-0 flex-col gap-4 transition-opacity ${
              isRefreshingEmails ? "pointer-events-none opacity-60" : ""
            }`}
          >
            <div className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex gap-2 items-center">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  className="cursor-pointer"
                  disabled={emailsLoading}
                  checked={allVisibleSelected}
                  onChange={(e) => {
                    toggleVisibleSelection(e.target.checked);
                  }}
                />
                <p>
                  Seleziona pagina
                  {selectedCount > 0 ? ` (${selectedCount})` : ""}
                </p>
              </div>

              <button
                className="flex w-fit items-center gap-2 rounded-lg border px-3 py-2 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
                onClick={async (event) => {
                  event.stopPropagation();
                  if (confirm("Clicca conferma per archiviare i selezionati")) {
                    await batchArchiveEmails(Array.from(selectedIds));
                    fetchEmails(emailsPage, errorTypes);
                  }
                }}
                disabled={selectedCount === 0 || emailsLoading}
                aria-busy={emailsLoading}
              >
                <Archive className="h-4 w-4" />
                <span>Archivia selezionati</span>
              </button>
            </div>
            {emails.map((email, index) => (
              <div
                key={email._id}
                className="flex min-w-0 flex-col gap-3 rounded-lg border p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <input
                    type="checkbox"
                    className="cursor-pointer"
                    disabled={emailsLoading}
                    checked={selectedIds.has(email._id)}
                    onChange={(e) => {
                      toggleEmailSelection(
                        email._id,
                        e.target.checked,
                        index
                      );
                    }}
                  />
                  <Link
                    className="flex min-w-0 flex-1 items-center gap-3"
                    to={`/admin/ai/${email._id}`}
                    target="_blank"
                  >
                    <div
                      className="shrink-0 rounded-full px-3 py-1 text-xs font-medium"
                      style={{
                        backgroundColor:
                          email.status === "success"
                            ? "#9AFEE3"
                            : email.status === "error"
                            ? "#ff746c"
                            : "#ddd",
                      }}
                    >
                      #
                      {email.status === "error"
                        ? email.errorType
                        : email.status}
                    </div>
                    <p className="truncate">
                      {email.encryptedContent.headers.subject}
                    </p>
                  </Link>
                </div>
                <div className="flex shrink-0 items-center justify-between gap-4 sm:justify-end">
                  <p className="text-xs text-gray-500 whitespace-nowrap">
                    {moment(email._createdAt).format("DD/MM/YYYY hh:mm:ss")}
                  </p>
                  <button
                    onClick={async (event) => {
                      event.stopPropagation();
                      if (confirm("Clicca conferma per archiviare")) {
                        await archiveEmail(email._id);
                        fetchEmails(emailsPage, errorTypes);
                        return;
                      }
                    }}
                    className="rounded-md p-2 hover:bg-muted"
                    disabled={emailsLoading}
                    aria-label="Archivia email"
                  >
                    <Archive className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div
            className={`flex min-w-0 flex-col items-center gap-3 transition-opacity sm:flex-row sm:justify-between ${
              isRefreshingEmails ? "opacity-60" : ""
            }`}
          >
            <p className="text-sm text-muted-foreground">
              {isRefreshingEmails
                ? "Aggiornamento..."
                : `Pagina ${emailsPage} di ${totalPages} - ${totalEmails} email`}
            </p>
            <Pagination className="mx-0 w-auto">
              <PaginationContent className="flex-wrap justify-center">
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    className={
                      emailsPage <= 1 || emailsLoading
                        ? "pointer-events-none opacity-50"
                        : ""
                    }
                    onClick={(event) => {
                      event.preventDefault();
                      setEmailsPage((p) => Math.max(p - 1, 1));
                    }}
                  />
                </PaginationItem>

                {paginationItems.map((item, index) =>
                  item === "ellipsis" ? (
                    <PaginationItem key={`ellipsis-${index}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={item}>
                      <PaginationLink
                        href="#"
                        isActive={emailsPage === item}
                        onClick={(event) => {
                          event.preventDefault();
                          setEmailsPage(item);
                        }}
                      >
                        {item}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}

                <PaginationItem>
                  <PaginationNext
                    href="#"
                    className={
                      emailsPage >= totalPages || emailsLoading
                        ? "pointer-events-none opacity-50"
                        : ""
                    }
                    onClick={(event) => {
                      event.preventDefault();
                      setEmailsPage((p) => Math.min(p + 1, totalPages));
                    }}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </>
      ) : (
        <div className="rounded-lg border p-8 text-center text-muted-foreground">
          Nessuna email trovata per i filtri selezionati.
        </div>
      )}
    </div>
  );
};

export default Ai;
